import axios from 'axios';
import cn from 'classnames';
import { PostMediaResult, User } from 'common/interfaces/api';
import FileSelect from 'components/common/FileSelect';
import { PenIcon } from 'components/v2/atoms/icon';
import ImageEditor from 'components/v2/molecules/image-editor';
import { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

interface ProfileAvatarPropsType {
  user: User;
  isCurrentUser: boolean;
  isSettingsPage: boolean;
  onImagePreview: (imageUrl: string, isAvatar: boolean) => void;
}

const ProfileAvatar = ({
  user,
  isCurrentUser,
  isSettingsPage,
  onImagePreview,
}: ProfileAvatarPropsType) => {
  const refetchProfile = useStore((state) => state.refetchProfile);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );

  // Avatar image editor state
  const [openAvatarImageEditor, setOpenAvatarImageEditor] = useState(false);
  const [avatarImageMediaUrl, setAvatarImageMediaUrl] = useState<string>();

  // Avatar upload mutations
  const { mutate: updateProfileAvatar } = useMutation(
    (url: string) => {
      return axios.post(`/profiles/avatars`, {
        avatar: url,
      });
    },
    {
      onSuccess: () => {
        void refetchProfile();
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'アバター画像の更新が失敗されました',
        });
      },
    },
  );

  const { mutate: uploadAvatarImage, isLoading: isUploadingAvatarImage } =
    useMutation(
      async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return (
          await axios.post(`/posts/media`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        ).data as PostMediaResult;
      },
      {
        onSuccess: (data) => {
          updateProfileAvatar(data?.data?.data.url);
        },
        onError: () => {
          setSnackbarMessage({
            type: 'error',
            text: 'アバター画像のアップロードが失敗されました',
          });
        },
      },
    );

  const handleAvatarImageSelect = useCallback((file: File) => {
    setAvatarImageMediaUrl(URL.createObjectURL(file));
    setOpenAvatarImageEditor(true);
  }, []);

  return (
    <div className="relative mt-[-60px]">
      {/* Avatar with status border */}
      <div
        className={cn(
          'size-[120px] overflow-hidden rounded-full bg-pp-bg-secondary',
          {
            // Default border
            'border-4 border-pp-bg-primary':
              !user.has_new_post && !user.has_tipped && !user.in_live,
            // Status borders with padding
            'border-[4px] p-[2px]':
              user.has_new_post || Boolean(user.has_tipped) || user.in_live,
            // Border colors based on status
            'border-[#4ae5c9]': user.has_new_post,
            'border-[#09C1D4]': user.has_tipped === 1,
            'border-[#B381FF]': user.has_tipped === 2,
            'border-[#F9B48D]': user.has_tipped === 3,
            'border-[#c3c3c3]': user.has_tipped === 4,
            'border-[#ddba04]': user.has_tipped === 5,
            'border-pp-error-primary': user.in_live,
          },
        )}
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.name}
            className="size-full cursor-pointer rounded-full object-cover"
            onClick={() => onImagePreview(user.avatar_url, true)}
          />
        ) : (
          <div className="flex size-full items-center justify-center rounded-full bg-pp-bg-tertiary">
            <span className="text-4xl text-pp-text-tertiary">
              {user.name?.charAt(0) || '?'}
            </span>
          </div>
        )}
      </div>

      {/* LIVE Badge */}
      {!isSettingsPage && user.in_live && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-[-4px]">
          <div className="flex h-6 w-12 items-center justify-center rounded border border-white bg-pp-error-primary text-[10px] font-semibold text-white">
            LIVE
          </div>
        </div>
      )}

      {/* Edit Avatar Button - only show for current user on settings page */}
      {isCurrentUser && isSettingsPage && (
        <FileSelect
          id="avatar-image-upload"
          onFileSelect={handleAvatarImageSelect}
          accept=".jpeg,.jpg,.png"
          className="absolute bottom-0 right-0 z-10 cursor-pointer"
        >
          <div
            className={cn(
              'flex size-8 items-center justify-center rounded-full border border-white bg-pp-overlay-dark text-white shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:bg-pp-overlay-dark-hover',
              {
                'pointer-events-none opacity-50': isUploadingAvatarImage,
              },
            )}
          >
            <PenIcon className="size-3" />
          </div>
        </FileSelect>
      )}

      {/* Avatar Image Editor */}
      {openAvatarImageEditor && avatarImageMediaUrl && isSettingsPage && (
        <ImageEditor
          open={openAvatarImageEditor}
          setOpen={setOpenAvatarImageEditor}
          mediaUrl={avatarImageMediaUrl}
          fixedRatio={1}
          freeCrop={false}
          onEditComplete={(file) => {
            uploadAvatarImage(file);
            setOpenAvatarImageEditor(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileAvatar;
